/**
 * The SharingApi for the web/desktop shell (one per app container): HTTP in the real app, the in-browser store in
 * mock mode (code-split, loaded on first use). `publicSharingApi` serves the signed-out /s/:token page.
 */
import type { SharingApi } from "@bundle/core";
import type { Container } from "../container.ts";
import { createSharingApi } from "./api.ts";

const byContainer = new WeakMap<Container, SharingApi>();

/** An object whose methods wait for `target` (the mock implementation loads lazily). */
function deferred<T extends object>(target: Promise<T>): T {
  return new Proxy({} as T, {
    get: (_obj, key) => (key === "then" ? undefined : (...args: unknown[]) => target.then((t) => (t[key as keyof T] as (...a: unknown[]) => unknown)(...args))),
  });
}

export function sharingPlatform(container: Container): SharingApi {
  const existing = byContainer.get(container);
  if (existing !== undefined) return existing;
  const api = container.mock
    ? deferred<SharingApi>(import("../mock/sharing.ts").then((m) => m.createMockSharingApi(() => container.session.state.session?.token)))
    : createSharingApi(container.session);
  byContainer.set(container, api);
  return api;
}
